import { Tooltip, Typography } from "antd";
import { CustomTitle } from "../components/TableComponent/styles";
import { useState } from "react";
import EditableCell from "../components/EditableCellComponent";
import "./tableUtils.css";
const { Paragraph } = Typography;

type SorterType = "numeric" | "string" | "date" | "isoDate" | "boolean";

type DataType = Record<string, any>;

interface ColumnProps<T extends DataType> {
	title: string;
	dataIndex: string;
	width?: number;
	sorterType?: SorterType;
	render?: (text: string, record: T) => JSX.Element | string;
	fixed?: "left" | "right";
	onCell?: (record: T) => Record<string, any>;
	editable?: boolean;
	handleSave?: (record: T, newValue: any) => void;
	recordRef?: React.MutableRefObject<T>;
	inputType?: "number" | "text";
}

const parseDate = (dateStr: string): Date => {
	const [day, month, year] = dateStr.split("-").map(Number);
	return new Date(year, month - 1, day);
};


const sorters: Record<SorterType, <T extends DataType>(a: T, b: T, dataIndex: string) => number> = {
	numeric: (a, b, dataIndex) => {
		const aValue = a[dataIndex] != null ? a[dataIndex] : 0;
		const bValue = b[dataIndex] != null ? b[dataIndex] : 0;
		return aValue - bValue;
	},
	string: (a, b, dataIndex) => {
		const aValue = String(a[dataIndex] ?? "");
		const bValue = String(b[dataIndex] ?? "");
		return aValue.localeCompare(bValue);
	},
	date: (a, b, dataIndex) => {
		const aDate = a[dataIndex] ? parseDate(a[dataIndex]) : new Date(0);
		const bDate = b[dataIndex] ? parseDate(b[dataIndex]) : new Date(0);
		return aDate.getTime() - bDate.getTime();
	},
	isoDate: (a, b, dataIndex) => {
		const aDate = a[dataIndex] ? new Date(a[dataIndex]).toISOString() : "";
		const bDate = b[dataIndex] ? new Date(b[dataIndex]).toISOString() : "";
		return aDate.localeCompare(bDate);
	},
	boolean: (a, b, dataIndex) => {
		const aValue = a[dataIndex] ? 1 : 0;
		const bValue = b[dataIndex] ? 1 : 0;
		return aValue - bValue;
	}
};

const TooltipParagraph: React.FC<{ text: string }> = ({ text }) => {
	const [truncated, setTruncated] = useState(false);
	return (
		<Tooltip title={truncated ? text : undefined}>
			<Paragraph ellipsis={{ onEllipsis: setTruncated }}>
				{text}
			</Paragraph>
		</Tooltip>
	);
};


export const createColumn = <T extends DataType>({
	title,
	dataIndex,
	width,
	sorterType,
	fixed,
	render,
	editable,
	handleSave,
	inputType,
}: ColumnProps<T>) => ({
		title: <CustomTitle>{title}</CustomTitle>,
		dataIndex,
		ellipsis: {
			showTitle: false,
		},
		width,
		fixed,
		sorter: sorterType ? (a: T, b: T) => sorters[sorterType](a, b, dataIndex) : undefined,
		render: (text: any, record: T) => {
			const isEditable = editable && !record.isFiscalYearCompleted;

			if (isEditable) {
				return (
					<EditableCell
						title={title.toString()}
						editable={isEditable}
						record={record}
						handleSave={handleSave as any}
						inputType={inputType}
					>
						{text}
					</EditableCell>
				);
			} else {
				return render ? render(text, record) : <TooltipParagraph text={text} />;
			}
		},
	});